import React, {
  useState,
  useEffect,
  useRef,
} from "react";

import {
  FiX,
  FiSend,
  FiCpu,
  FiChevronDown,
} from "react-icons/fi";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import axios from "axios";

import assistent from "../../assets/assistent.png";

// ======================================================
// ANIMACIONES
// ======================================================

const chatVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.92,
    transformOrigin: "bottom right",
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      type: "spring",
      stiffness: 300,
      damping: 28,
    },
  },

  exit: {
    opacity: 0,
    y: 30,
    scale: 0.94,

    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

const messageVariants = {
  hidden: {
    opacity: 0,
    y: 12,
    scale: 0.97,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
    },
  },
};

const tooltipVariants = {
  hidden: {
    opacity: 0,
    y: 8,
    scale: 0.95,
  },

  visible: {
    opacity: 1,
    y: 0,
    scale: 1,

    transition: {
      duration: 0.18,
    },
  },

  exit: {
    opacity: 0,
    y: 6,
    scale: 0.95,

    transition: {
      duration: 0.14,
    },
  },
};

// ======================================================
// DOTS ESCRIBIENDO
// ======================================================

const TypingDots = () => (
  <div className="flex items-center gap-[5px] py-1 px-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-2 h-2 rounded-full bg-[#C8A98D]"
        animate={{
          y: [0, -5, 0],
          opacity: [0.4, 1, 0.4],
        }}
        transition={{
          duration: 0.9,
          repeat: Infinity,
          delay: i * 0.18,
          ease: "easeInOut",
        }}
      />
    ))}
  </div>
);

const ChatBot = () => {
  const [chatAbierto, setChatAbierto] =
    useState(false);

  const [mensajes, setMensajes] = useState([
    {
      texto:
        "Hola 😊 soy el asistente de Aldana Selena Tattoo. Estoy acá para ayudarte con turnos, ideas, estilos y cualquier consulta.",

      origen: "asistente",
    },
  ]);

  const [nuevoMensaje, setNuevoMensaje] =
    useState("");

  const [enviandoMensaje, setEnviandoMensaje] =
    useState(false);

  const [mostrarTooltip, setMostrarTooltip] =
    useState(false);

  const [scrollVisible, setScrollVisible] =
    useState(false);

  const mensajesRef = useRef(null);

  const inputRef = useRef(null);

  // ======================================================
  // AUTO SCROLL
  // ======================================================

  useEffect(() => {
    const el = mensajesRef.current;

    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: "smooth",
    });
  }, [mensajes]);

  // ======================================================
  // SCROLL INDICATOR
  // ======================================================

  useEffect(() => {
    const el = mensajesRef.current;

    if (!el) return;

    const handleScroll = () => {
      const distFromBottom =
        el.scrollHeight -
        el.scrollTop -
        el.clientHeight;

      setScrollVisible(distFromBottom > 80);
    };

    el.addEventListener(
      "scroll",
      handleScroll
    );

    return () =>
      el.removeEventListener(
        "scroll",
        handleScroll
      );
  }, [chatAbierto]);

  // ======================================================
  // FOCUS INPUT
  // ======================================================

  useEffect(() => {
    if (chatAbierto) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [chatAbierto]);

  // ======================================================
  // SCROLL TO BOTTOM
  // ======================================================

  const scrollAlFondo = () => {
    mensajesRef.current?.scrollTo({
      top: mensajesRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  // ======================================================
  // ENVIAR MENSAJE
  // ======================================================

  const handleEnviarMensaje = async () => {
    if (
      !nuevoMensaje.trim() ||
      enviandoMensaje
    )
      return;

    const texto = nuevoMensaje.trim();

    setNuevoMensaje("");

    setEnviandoMensaje(true);

    // mensaje usuario + temporal
    setMensajes((prev) => [
      ...prev,

      {
        texto,
        origen: "usuario",
      },

      {
        texto: "",
        origen: "asistente",
        temporal: true,
      },
    ]);

    try {
      const respuesta =
        await obtenerRespuestaDesdeServidor(
          texto
        );

      setMensajes((prev) =>
        prev.map((m) =>
          m.temporal
            ? {
                texto: respuesta,
                origen: "asistente",
              }
            : m
        )
      );
    } catch (error) {
      const textoError =
        error?.message ||
        "Hubo un problema al conectar con el asistente.";

      setMensajes((prev) =>
        prev.map((m) =>
          m.temporal
            ? {
                texto: `Error: ${textoError}`,
                origen: "asistente",
                isError: true,
              }
            : m
        )
      );
    } finally {
      setEnviandoMensaje(false);

      inputRef.current?.focus();
    }
  };

  // ======================================================
  // API
  // ======================================================

  const obtenerRespuestaDesdeServidor =
    async (mensajeUsuario) => {
      // ⚠️ IMPORTANTE:
      // CAMBIÁ ESTE ENDPOINT
      // por el archivo PHP REAL

      const endpoint =
        "https://aldanaselena.com/bot.php?recurso=apiai";
 
      try {
        const resp = await axios.post(
          endpoint,
          {
            mensaje: mensajeUsuario,
          },
          {
            timeout: 20000,

            headers: {
              "Content-Type":
                "application/json",
            },
          }
        );

        console.log(
          "RESPUESTA SERVIDOR:",
          resp.data
        );

        // error backend
        if (resp.data?.error) {
          throw new Error(resp.data.error);
        }

        // respuesta válida
        if (
          typeof resp.data?.respuesta ===
          "string"
        ) {
          return resp.data.respuesta;
        }

        // debug
        throw new Error(
          "Respuesta inválida del servidor"
        );
      } catch (err) {
        console.error(err);

        // backend respondió error
        if (err.response) {
          throw new Error(
            err.response.data?.error ||
              `Error ${err.response.status} del servidor`
          );
        }

        // nunca respondió
        else if (err.request) {
          throw new Error(
            "El servidor no respondió"
          );
        }

        // otro error
        else {
          throw new Error(err.message);
        }
      }
    };

  // ======================================================
  // ENTER
  // ======================================================

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey
    ) {
      e.preventDefault();

      handleEnviarMensaje();
    }
  };

  // ======================================================
  // CHAT
  // ======================================================

  const handleChatToggle = () => {
    setChatAbierto(!chatAbierto);
  };

  const handleCloseChat = () => {
    setChatAbierto(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 font-montserrat select-none">

      {/* ====================================================== */}
      {/* BOTÓN FLOTANTE */}
      {/* ====================================================== */}

      <AnimatePresence>
        {!chatAbierto && (
          <motion.button
            initial={{
              opacity: 0,
              scale: 0.7,
              rotate: -10,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.7,
              rotate: 10,
            }}
            whileHover={{
              scale: 1.08,
            }}
            whileTap={{
              scale: 0.93,
            }}
            transition={{
              type: "spring",
              stiffness: 320,
              damping: 22,
            }}
            onClick={handleChatToggle}
            onMouseEnter={() =>
              setMostrarTooltip(true)
            }
            onMouseLeave={() =>
              setMostrarTooltip(false)
            }
            className="
              relative
              flex
              items-center
              justify-center
              w-16
              h-16
              rounded-full
              bg-gradient-to-br
              from-[#F5EFE8]
              to-[#E8D8C8]
              shadow-[0_8px_32px_rgba(0,0,0,0.22)]
              border
              border-[#E6D7CA]
              overflow-hidden
            "
          >
            {/* PULSE */}
            <motion.span
              className="
                absolute
                inset-0
                rounded-full
                border-2
                border-[#C8A98D]/40
              "
              animate={{
                scale: [1, 1.35],
                opacity: [0.6, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeOut",
              }}
            />

            <img
              src={assistent}
              alt="Asistente"
              className="
                relative
                z-10
                w-11
                h-11
                object-contain
              "
            />

           
          </motion.button>
        )}
      </AnimatePresence>

      {/* ====================================================== */}
      {/* TOOLTIP */}
      {/* ====================================================== */}

      <AnimatePresence>
        {mostrarTooltip &&
          !chatAbierto && (
            <motion.div
              variants={tooltipVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="
                absolute
                bottom-20
                right-0
                bg-[#E8D8C8]
                text-[#3E2F26]
                px-4
                py-3
                rounded-2xl
                shadow-xl
                border
                border-[#D8C5B5]
                w-64
                backdrop-blur-xl
              "
            >
              <div className="flex items-start gap-3">

                <div
                  className="
                    mt-1
                    w-3
                    h-3
                    rounded-full
                    bg-green-400
                    shadow-[0_0_12px_rgba(74,222,128,0.9)]
                  "
                />

                <div>
                  <p className="text-sm font-semibold">
                    Asistente de Aldana
                  </p>

                  <p className="text-xs mt-1 leading-relaxed text-[#5C4638]">
                    Disponible ahora para ayudarte
                    con ideas, reservas y
                    consultas.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
      </AnimatePresence>

      {/* ====================================================== */}
      {/* CHAT */}
      {/* ====================================================== */}

      <AnimatePresence>
        {chatAbierto && (
          <motion.div
            variants={chatVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="
              relative
              w-[340px]
              max-w-[92vw]
              h-[540px]
              max-h-[78vh]
              bg-[#F5EFE8]
              border
              border-[#E5D7CB]
              rounded-[28px]
              shadow-[0_25px_80px_rgba(0,0,0,0.35)]
              overflow-hidden
              flex
              flex-col
              backdrop-blur-2xl
            "
          >

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <div
              className="
                relative
                px-5
                py-4
                border-b
                border-[#E5D7CB]
                bg-[#F8F3EE]
              "
            >
              <div className="flex items-center justify-between">

                <div className="flex items-center gap-3">

                  {/* AVATAR */}
                  <div className="relative">

                    <div
                      className="
                        w-11
                        h-11
                        rounded-full
                        bg-[#E9DDD2]
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                      "
                    >
                      <img
                        src={assistent}
                        alt="Asistente"
                        className="
                          w-8
                          h-8
                          object-contain
                        "
                      />
                    </div>

                    {/* LED */}
                    <motion.div
                      className="
                        absolute
                        bottom-0
                        right-0
                        w-3
                        h-3
                        rounded-full
                        bg-green-400
                        border
                        border-white
                      "
                      animate={{
                        boxShadow: [
                          "0 0 0px rgba(74,222,128,0)",
                          "0 0 12px rgba(74,222,128,1)",
                          "0 0 0px rgba(74,222,128,0)",
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                      }}
                    />
                  </div>

                  {/* INFO */}
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-[#2E221B] font-semibold text-[15px]">
                        Asistente
                      </h2>

                      <FiCpu
                        className="text-[#8C6E5B]"
                        size={14}
                      />
                    </div>

                    <p className="text-xs text-[#7B6759]">
                      Aldana Selena Tattoo
                    </p>
                  </div>
                </div>

                {/* CLOSE */}
                <motion.button
                  whileHover={{
                    rotate: 90,
                    scale: 1.1,
                  }}
                  whileTap={{
                    scale: 0.9,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 20,
                  }}
                  onClick={handleCloseChat}
                  className="
                    w-8
                    h-8
                    rounded-full
                    bg-[#EDE4DB]
                    hover:bg-[#E0D0C2]
                    flex
                    items-center
                    justify-center
                    text-[#7B6759]
                    transition-colors
                  "
                >
                  <FiX size={16} />
                </motion.button>
              </div>
            </div>

            {/* ====================================================== */}
            {/* MENSAJES */}
            {/* ====================================================== */}

            <div className="relative flex-1 overflow-hidden">

              <div
                ref={mensajesRef}
                className="
                  h-full
                  overflow-y-auto
                  px-4
                  py-4
                  space-y-3
                  flex
                  flex-col
                  scrollbar-thin
                "
              >
                {mensajes.map(
                  (mensaje, index) => (
                    <motion.div
                      key={index}
                      variants={
                        messageVariants
                      }
                      initial="hidden"
                      animate="visible"
                      className={`
                        px-4
                        py-3
                        rounded-[22px]
                        max-w-[82%]
                        text-[15px]
                        leading-relaxed
                        break-words

                        ${
                          mensaje.origen ===
                          "usuario"
                            ? `
                              bg-[#C9A98E]
                              text-white
                              self-end
                              rounded-br-md
                              shadow-lg
                            `
                            : mensaje.isError
                            ? `
                              bg-[#FFF1EF]
                              text-[#C0392B]
                              self-start
                              border
                              border-[#FFD5CF]
                              rounded-bl-md
                            `
                            : `
                              bg-white
                              text-[#3E2F26]
                              self-start
                              border
                              border-[#E5D7CB]
                              rounded-bl-md
                              shadow-sm
                            `
                        }
                      `}
                    >
                      {mensaje.temporal ? (
                        <TypingDots />
                      ) : (
                        mensaje.texto
                      )}
                    </motion.div>
                  )
                )}
              </div>

              {/* ====================================================== */}
              {/* BOTÓN SCROLL */}
              {/* ====================================================== */}

              <AnimatePresence>
                {scrollVisible && (
                  <motion.button
                    initial={{
                      opacity: 0,
                      y: 8,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      y: 8,
                    }}
                    onClick={scrollAlFondo}
                    className="
                      absolute
                      bottom-3
                      right-3
                      w-8
                      h-8
                      rounded-full
                      bg-white
                      border
                      border-[#E5D7CB]
                      shadow-md
                      flex
                      items-center
                      justify-center
                      text-[#7B6759]
                      hover:text-[#3E2F26]
                      transition-colors
                    "
                  >
                    <FiChevronDown
                      size={16}
                    />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* ====================================================== */}
            {/* INPUT */}
            {/* ====================================================== */}

            <div
              className="
                p-4
                border-t
                border-[#E5D7CB]
                bg-[#F8F3EE]
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2
                  w-full
                "
              >

                {/* INPUT */}
                <input
                  ref={inputRef}
                  type="text"
                  value={nuevoMensaje}
                  onChange={(e) =>
                    setNuevoMensaje(
                      e.target.value
                    )
                  }
                  onKeyDown={
                    handleKeyDown
                  }
                  placeholder="Escribí tu idea..."
                  disabled={
                    enviandoMensaje
                  }
                  className="
                    flex-1
                    min-w-0
                    h-[48px]
                    rounded-full
                    border
                    border-[#D8C7B8]
                    bg-[#F5EFE8]
                    px-5
                    text-[#3E2F26]
                    placeholder:text-[#9E8D80]
                    focus:outline-none
                    focus:ring-2
                    focus:ring-[#C8A98D]
                  "
                />

                {/* BOTÓN ENVIAR */}
                <motion.button
                  onClick={
                    handleEnviarMensaje
                  }
                  disabled={
                    enviandoMensaje ||
                    !nuevoMensaje.trim()
                  }
                  whileHover={{
                    scale: 1.06,
                  }}
                  whileTap={{
                    scale: 0.92,
                  }}
                  className="
                    flex-shrink-0
                    w-[48px]
                    h-[48px]
                    rounded-full
                    bg-[#C8A98D]
                    hover:bg-[#B89578]
                    text-white
                    flex
                    items-center
                    justify-center
                    transition-all
                    duration-200
                    shadow-lg
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                  "
                >
                  <AnimatePresence mode="wait">
                    {enviandoMensaje ? (
                      <motion.span
                        key="loading"
                        initial={{
                          opacity: 0,
                          rotate: -90,
                        }}
                        animate={{
                          opacity: 1,
                          rotate: 0,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                        className="
                          w-4
                          h-4
                          border-2
                          border-white/40
                          border-t-white
                          rounded-full
                          animate-spin
                          block
                        "
                      />
                    ) : (
                      <motion.span
                        key="send"
                        initial={{
                          opacity: 0,
                          x: -4,
                        }}
                        animate={{
                          opacity: 1,
                          x: 0,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                      >
                        <FiSend
                          size={18}
                        />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatBot;
